import { Rythme1 } from "@/components/newIcons/rythmes/Rythme1";
import { Rythme2 } from "@/components/newIcons/rythmes/Rythme2";
import { Rythme3 } from "@/components/newIcons/rythmes/Rythme3";
import { Rythme4 } from "@/components/newIcons/rythmes/Rythme4";
import { Rythme5 } from "@/components/newIcons/rythmes/Rythme5";
import { useStore } from "@nanostores/react";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { dataStore, filteredValuesStore, setTypeValues, updateUrl } from "../../_utils/searchStore";
import type { Value } from "../../_utils/types";
import { Collapse } from "./Collapse";
import { MultiSelect } from "./MultiSelect";
import { drawerTypeStore } from "./store";

const ALL_RYTHMES = [
  {
    key: "1",
    label: "Niveau 1",
    text: "Accessible à tous",
    icon: <Rythme1 className="w-7 h-7" />,
  },
  {
    key: "2",
    label: "Niveau 2",
    text: "Modéré",
    icon: <Rythme2 className="w-7 h-7" />,
  },
  {
    key: "3",
    label: "Niveau 3",
    text: "Plus soutenu",
    icon: <Rythme3 className="w-7 h-7" />,
  },
  {
    key: "4",
    label: "Niveau 4",
    text: "Grand voyageur",
    icon: <Rythme4 className="w-7 h-7" />,
  },
  {
    key: "5",
    label: "Niveau 5",
    text: "Intense",
    icon: <Rythme5 className="w-7 h-7" />,
  },
];

type Props = {
  isDesktop?: boolean;
};
export const Rythme = ({ isDesktop = false }: Props) => {
  const router = useRouter();
  const state = useStore(drawerTypeStore);
  const selectedRythmes = useStore(filteredValuesStore("rythme"));
  const [localRythmes, setLocalRythmes] = useState(selectedRythmes);
  const selectedRythmesRef = useRef(selectedRythmes);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldT = selectedRythmesRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newT = selectedRythmes.sort((a, b) => a.value.localeCompare(b.value));

      if (oldT.length !== newT.length) return true;

      for (let index = 0; index < newT.length; index++) {
        if (oldT[index] !== newT[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedRythmesRef.current = selectedRythmes;
      setLocalRythmes(selectedRythmes);
    }
  }, [selectedRythmes]);

  const handleChange = (newValue: Value, directUpdate = false) => {
    let newRythmes: Array<Value> = [];
    if (localRythmes.some((lr) => lr.value === newValue.value)) {
      newRythmes = localRythmes.filter((lr) => lr.value !== newValue.value);
    } else {
      newRythmes = [...localRythmes, newValue];
    }

    setLocalRythmes(newRythmes);

    if (directUpdate) {
      setTypeValues("rythme", newRythmes);
      updateUrl(router);
    }
  };

  const handleOpen = () => {
    drawerTypeStore.set(state === "rythmes" ? null : "rythmes");
  };

  const handleClickOutside = () => {
    setTypeValues("rythme", localRythmes);

    drawerTypeStore.set(null);
  };

  const handleValidate = () => {
    setTypeValues("rythme", localRythmes);
    updateUrl(router);
  };

  const handleClear = () => {
    setTypeValues("rythme", []);
    updateUrl(router);
  };

  const rythmes = [
    ...selectedRythmes
      .filter((s) => !data?.rythmes.some((t) => t.key === s.value))
      .map((s) => ({
        value: s.value,
      })),
    ...(data?.rythmes.map((t) => ({ value: t.key })) || []),
  ];

  return isDesktop ? (
    <MultiSelect
      placeholder="Rythme"
      type="rythme"
      onChange={handleChange}
      onValidate={handleValidate}
      onClear={handleClear}
      localValues={localRythmes}
      selectedValues={selectedRythmes}
      values={ALL_RYTHMES.filter((r) => rythmes.some((rythme) => rythme.value === r.key)).map((d) => ({
        label: d.text,
        value: d.key,
      }))}
      disableSort
      disableSearch
    />
  ) : (
    <Collapse
      open={state === "rythmes"}
      title="Rythme"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedRythmes.length}
    >
      <div className="flex flex-wrap -m-[10px] pt-5">
        {ALL_RYTHMES.filter((r) => rythmes.some((rythme) => rythme.value === r.key)).map((data) => {
          const isActive = localRythmes.some((lt) => lt.value === data.key);

          return (
            <div
              key={data.key}
              className={clsx(
                "w-24 border rounded-sm p-2 text-xs leading-5 flex flex-col justify-center items-center m-[10px] cursor-pointer",
                {
                  "border-primary bg-[#FBF7F4]": isActive,
                  "border-[#E5E5E5]": !isActive,
                },
              )}
              onClick={() =>
                handleChange({
                  value: data.key,
                  type: "rythme",
                })
              }
            >
              {data.icon}
              <p className="font-bold whitespace-nowrap">{data.label}</p>
              <p className="whitespace-nowrap">{data.text}</p>
            </div>
          );
        })}
      </div>
    </Collapse>
  );
};
